import styled, { css } from 'styled-components';
import type { DefaultTheme } from 'styled-components';
import { Heading, Text } from '@makemydeal/dr-ui-bricks-components';
import { BreakPoints } from '@makemydeal/dr-ui-bricks-utils';

const getFontColor = ({ theme }: { theme: DefaultTheme }) => theme.uiBricks?.commonCardIconColor || '#000000';

// Spacing is owned entirely by `gap` on the nesting levels below rather than by margins on the
// individual pieces, so a subsection's spacing no longer depends on what happens to follow it.
const SECTION_GAP = '24px';
const SUBSECTION_GAP = '16px';
const CONTENT_GAP = '8px';

/** A vertical stack with a fixed gap; shared as css so both the div wrappers and the ul can use it. */
const verticalStack = (gap: string) => css`
    display: flex;
    flex-direction: column;
    gap: ${gap};
`;

export const ContentWrapper = styled.div`
    ${verticalStack(SECTION_GAP)}
    padding: 8px 0;
`;

export const SectionBlock = styled.div`
    ${verticalStack(SUBSECTION_GAP)}
`;

/** One subsection: its header plus the grid/list of entries underneath. */
export const SubSectionBlock = styled.div`
    ${verticalStack(CONTENT_GAP)}
`;

/** A column of subsections inside one of the two-column layouts. */
export const SubSectionColumn = styled.div`
    ${verticalStack(SUBSECTION_GAP)}
`;

export const SectionHeader = styled(Heading)`
    margin: 0;
    color: ${getFontColor};
`;

export const SubSectionHeader = styled(Heading)`
    margin: 0;
    color: ${getFontColor};
`;

export const SpecGrid = styled.div`
    display: grid;
    grid-template-columns: auto 1fr;
    row-gap: ${CONTENT_GAP};
    column-gap: 24px;
`;

export const SpecTwoColumnLayout = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 24px;
    row-gap: ${SUBSECTION_GAP};

    @media (max-width: ${BreakPoints.PHONE}) {
        grid-template-columns: 1fr;
    }
`;

export const FeaturesTwoColumnLayout = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 24px;
    row-gap: ${SUBSECTION_GAP};

    @media (max-width: ${BreakPoints.PHONE}) {
        grid-template-columns: 1fr;
    }
`;

export const SpecLabel = styled(Text)`
    color: ${getFontColor};
`;

export const SpecValue = styled(Text)`
    color: ${getFontColor};
    text-align: right;
`;

export const DimensionsSpecGrid = styled(SpecGrid)`
    ${SpecValue} {
        text-align: left;
    }
`;

export const FeatureList = styled.ul`
    ${verticalStack(CONTENT_GAP)}
    margin: 0;
    padding: 0;
    list-style: none;
`;

// Stays a real li for the list semantics and the bullet; FeatureItemLabel supplies the DS typography.
export const FeatureListItem = styled.li`
    padding-left: 12px;
    position: relative;
    break-inside: avoid;

    &::before {
        content: '•';
        position: absolute;
        top: 0;
        left: 0;
        color: ${getFontColor};
    }
`;

export const FeatureItemLabel = styled(Text)`
    color: ${getFontColor};
`;

export const EmptyState = styled(Text)`
    color: ${getFontColor};
    text-align: center;
    padding: 32px 0;
`;

export const SlideoutTitle = styled(Heading)`
    color: ${({ theme }) => theme.uiBricks?.primaryAccentColor || '#000000'};
`;

export const ColorValue = styled(Text)`
    color: ${getFontColor};
    text-align: right;
`;

export const Disclaimer = styled(Text)`
    padding-top: 16px;
    border-top: ${(props: { theme: DefaultTheme }) => `1px solid ${getFontColor(props)}`};
    color: ${getFontColor};
`;

export const ColorSwatch = styled.span<{ $color: string }>`
    display: inline-block;
    width: 16px;
    height: 16px;
    margin-right: 8px;
    vertical-align: middle;
    border-radius: 50%;
    background-color: ${({ $color }) => $color};
    border: ${({ theme }) => `1px solid ${theme.uiBricks?.defaultInputFieldDisabledFontColor || '#B2B2B2'}`};
`;
